import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, ChevronDown, RefreshCw, X, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:8003";
const TOKEN_KEY = "admin_token";

function getToken() { return sessionStorage.getItem(TOKEN_KEY); }
function setToken(t) { sessionStorage.setItem(TOKEN_KEY, t); }
function clearToken() { sessionStorage.removeItem(TOKEN_KEY); }
function authHeader() { return { Authorization: `Bearer ${getToken()}` }; }

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/admin/login`, { password });
      setToken(data.token);
      onLogin();
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center font-body">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 w-full max-w-sm">
        <div className="flex items-center justify-center w-12 h-12 bg-blue-50 rounded-xl mb-6 mx-auto">
          <Lock size={22} className="text-blue-600" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading text-xl font-bold text-slate-900 text-center mb-1">Admin Access</h1>
        <p className="text-sm text-slate-500 text-center mb-6">AutoShiftAI CRM</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter admin password"
            data-testid="admin-password-input"
            required
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

const STAGES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "prospect", label: "Prospect" },
  { key: "client", label: "Client" },
  { key: "work_accepted", label: "Work Accepted" },
  { key: "completed", label: "Completed" },
];

const STATUS_STYLES = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-blue-100 text-blue-700",
  prospect: "bg-yellow-100 text-yellow-700",
  client: "bg-purple-100 text-purple-700",
  work_accepted: "bg-orange-100 text-orange-700",
  completed: "bg-green-100 text-green-700",
};

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  prospect: "Prospect",
  client: "Client",
  work_accepted: "Work Accepted",
  completed: "Completed",
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getToken());
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [openStatusId, setOpenStatusId] = useState(null);

  function handleUnauth() {
    clearToken();
    setAuthed(false);
  }

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND}/api/contact`, { headers: authHeader() });
      data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setInquiries(data);
    } catch (err) {
      if (err.response?.status === 401) { handleUnauth(); return; }
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  // Sync notes when selected changes
  useEffect(() => {
    if (selected) setNotes(selected.notes || "");
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = activeStage === "all"
    ? inquiries
    : inquiries.filter(i => i.status === activeStage);

  const counts = STAGES.reduce((acc, s) => {
    acc[s.key] = s.key === "all" ? inquiries.length : inquiries.filter(i => i.status === s.key).length;
    return acc;
  }, {});

  async function updateStatus(id, status) {
    try {
      const { data } = await axios.patch(`${BACKEND}/api/contact/${id}`, { status }, { headers: authHeader() });
      setInquiries(prev => prev.map(i => i.id === id ? data : i));
      if (selected?.id === id) setSelected(data);
      toast.success(`Status updated to "${STATUS_LABELS[status]}"`);
    } catch (err) {
      if (err.response?.status === 401) { handleUnauth(); return; }
      toast.error("Failed to update status");
    }
    setOpenStatusId(null);
  }

  async function saveNotes(id) {
    setSavingNotes(true);
    try {
      const { data } = await axios.patch(`${BACKEND}/api/contact/${id}`, { notes }, { headers: authHeader() });
      setInquiries(prev => prev.map(i => i.id === id ? data : i));
      setSelected(data);
      toast.success("Notes saved");
    } catch (err) {
      if (err.response?.status === 401) { handleUnauth(); return; }
      toast.error("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  }

  async function deleteInquiry(id) {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${BACKEND}/api/contact/${id}`, { headers: authHeader() });
      setInquiries(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry deleted");
    } catch (err) {
      if (err.response?.status === 401) { handleUnauth(); return; }
      toast.error("Failed to delete inquiry");
    }
  }

  if (!authed) return <LoginScreen onLogin={() => { setAuthed(true); fetchInquiries(); }} />;

  return (
    <div className="min-h-screen bg-slate-50 font-body" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900">CRM — Contact Inquiries</h1>
          <p className="text-sm text-slate-500 mt-0.5">AutoShiftAI Admin</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={fetchInquiries}
            data-testid="refresh-btn"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            onClick={handleUnauth}
            data-testid="logout-btn"
            className="text-sm text-slate-400 hover:text-red-500 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Stage filter tabs */}
      <div className="bg-white border-b border-slate-200 px-6 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {STAGES.map(s => (
            <button
              key={s.key}
              data-testid={`stage-tab-${s.key}`}
              onClick={() => setActiveStage(s.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeStage === s.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {s.label}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeStage === s.key ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"
              }`}>
                {counts[s.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Main layout */}
      <div className="flex h-[calc(100vh-113px)]">
        {/* Table */}
        <div className={`flex-1 overflow-auto ${selected ? "hidden md:block" : ""}`}>
          {loading ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No inquiries in this stage.</div>
          ) : (
            <table className="w-full text-sm" data-testid="inquiries-table">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Industry</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Date</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(inquiry => (
                  <tr
                    key={inquiry.id}
                    data-testid={`inquiry-row-${inquiry.id}`}
                    onClick={() => setSelected(inquiry)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${
                      selected?.id === inquiry.id ? "bg-blue-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{inquiry.name}</div>
                      <div className="text-slate-500 text-xs">{inquiry.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{inquiry.company || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{inquiry.industry || "—"}</td>
                    <td className="px-4 py-3">
                      {/* Status dropdown */}
                      <div className="relative" onClick={e => e.stopPropagation()}>
                        <button
                          data-testid={`status-btn-${inquiry.id}`}
                          onClick={() => setOpenStatusId(openStatusId === inquiry.id ? null : inquiry.id)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[inquiry.status]}`}
                        >
                          {STATUS_LABELS[inquiry.status]}
                          <ChevronDown size={11} />
                        </button>
                        {openStatusId === inquiry.id && (
                          <div className="absolute z-20 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                            {STAGES.filter(s => s.key !== "all").map(s => (
                              <button
                                key={s.key}
                                data-testid={`status-option-${s.key}`}
                                onClick={() => updateStatus(inquiry.id, s.key)}
                                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 ${
                                  inquiry.status === s.key ? "font-semibold text-blue-600" : "text-slate-700"
                                }`}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{formatDate(inquiry.created_at)}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <button
                        data-testid={`delete-btn-${inquiry.id}`}
                        onClick={() => deleteInquiry(inquiry.id)}
                        className="text-slate-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="w-full md:w-96 bg-white border-l border-slate-200 flex flex-col overflow-hidden" data-testid="detail-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-heading font-semibold text-slate-900 text-base">{selected.name}</h2>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-slate-700"
                data-testid="close-panel-btn"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Contact info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Email</span>
                  <a href={`mailto:${selected.email}`} className="text-blue-600 hover:underline">{selected.email}</a>
                </div>
                {selected.phone && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone</span>
                    <span className="text-slate-800">{selected.phone}</span>
                  </div>
                )}
                {selected.company && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Company</span>
                    <span className="text-slate-800">{selected.company}</span>
                  </div>
                )}
                {selected.industry && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Industry</span>
                    <span className="text-slate-800">{selected.industry}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Received</span>
                  <span className="text-slate-800">{formatDate(selected.created_at)}</span>
                </div>
                {selected.updated_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Updated</span>
                    <span className="text-slate-800">{formatDate(selected.updated_at)}</span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Status</p>
                <div className="flex flex-wrap gap-2">
                  {STAGES.filter(s => s.key !== "all").map(s => (
                    <button
                      key={s.key}
                      data-testid={`panel-status-${s.key}`}
                      onClick={() => updateStatus(selected.id, s.key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selected.status === s.key
                          ? `${STATUS_STYLES[s.key]} border-current`
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Message</p>
                <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 leading-relaxed">{selected.message}</p>
              </div>

              {/* Notes */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Internal Notes</p>
                <textarea
                  data-testid="notes-input"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add internal notes..."
                  rows={5}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 placeholder:text-slate-400"
                />
                <button
                  data-testid="save-notes-btn"
                  onClick={() => saveNotes(selected.id)}
                  disabled={savingNotes}
                  className="mt-2 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingNotes ? "Saving..." : "Save Notes"}
                </button>
              </div>
            </div>

            {/* Delete */}
            <div className="px-5 py-4 border-t border-slate-100">
              <button
                data-testid="panel-delete-btn"
                onClick={() => deleteInquiry(selected.id)}
                className="w-full py-2 text-sm text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
              >
                Delete Inquiry
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Close dropdown on outside click */}
      {openStatusId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenStatusId(null)} />
      )}
    </div>
  );
}
